import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import UserMenu from './UserMenu';
import ThemeToggle from '../ThemeToggle';
import { useTenantStore } from '../../stores/tenantStore';
import { MenuItemType } from '../../config/app.config';

interface MobileMenuProps {
  visible: boolean;
  menuItems: MenuItemType[];
  selectedKey: string;
  userName?: string;
  userRole?: string;
  onClose: () => void;
  onMenuClick: (key: string) => void;
  onProfileClick: () => void;
  onCoinsClick: () => void;
  onLogout: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  visible,
  menuItems,
  selectedKey,
  userName,
  userRole,
  onClose,
  onMenuClick,
  onProfileClick,
  onCoinsClick,
  onLogout,
}) => {
  const handleMenuClick = (key: string) => {
    onMenuClick(key);
    onClose();
  };

  const { name: siteName, slogan: siteSlogan } = useTenantStore();

  return (
    <Transition show={visible} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        {/* 背景遮罩 */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        {/* 侧边栏容器 */}
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 left-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-xs">
                  <div className="flex h-full flex-col bg-bg-surface shadow-xl">
                    {/* 头部 */}
                    <div className="flex items-center justify-between p-4 border-b border-border-subtle">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-light rounded-xl flex items-center justify-center shadow-lg">
                          <Squares2X2Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-lg font-bold text-gray-50 font-serif">
                            {siteName}
                          </span>
                          <span className="text-xs text-gray-400 font-medium tracking-wide">
                            {siteSlogan}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={onClose}
                        className="rounded-lg p-2 hover:bg-bg-elevated hover:text-white text-gray-400 transition-all"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>

                    {/* 菜单 */}
                    <nav className="flex-1 px-3 py-4 overflow-y-auto">
                      <ul className="space-y-1">
                        {menuItems.map((item, index) => {
                          if (item.type === 'divider') {
                            return (
                              <li key={`divider-${index}`} className="py-2">
                                <div className="border-t border-border-default" />
                              </li>
                            );
                          }

                          const IconComponent = item.icon;
                          return (
                            <li key={item.key}>
                              <button
                                onClick={() => handleMenuClick(item.key)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${selectedKey === item.key
                                  ? 'bg-bg-elevated text-white font-medium'
                                  : 'text-gray-400 hover:text-gray-50 hover:bg-bg-elevated/50'
                                  }`}
                              >
                                <IconComponent className="w-5 h-5" />
                                <span className="font-medium">{item.label}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </nav>

                    {/* 底部用户信息 */}
                    <div className="p-4 border-t border-border-subtle space-y-3">
                      <ThemeToggle />
                      <UserMenu
                        userName={userName}
                        userRole={userRole}
                        onProfileClick={() => { onProfileClick(); onClose(); }}
                        onCoinsClick={() => { onCoinsClick(); onClose(); }}
                        onLogout={() => { onLogout(); onClose(); }}
                      />
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MobileMenu;
